import { Button, Input, PageHeader, Select } from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { UsersTable } from '@/features/users/UsersTable';
import type { UsersFilters } from '@/features/users/hooks';
import { useNavigate, useSearchParams } from '@solidjs/router';
import { Show, createMemo, createSignal, onCleanup } from 'solid-js';

export default function AdminUsers() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams<{ q?: string; status?: string }>();

  // Debounce search input 200ms before touching the URL (matches DeviceFilters).
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  const [q, setQ] = createSignal(params.q ?? '');
  const onSearchInput = (value: string) => {
    setQ(value);
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => setParams({ q: value || undefined }), 200);
  };
  onCleanup(() => clearTimeout(debounceTimer));

  const filters = createMemo<UsersFilters>(() => ({
    q: params.q || undefined,
    status:
      params.status === 'pending' || params.status === 'active' || params.status === 'disabled'
        ? params.status
        : undefined,
  }));

  const canInvite = () => session()?.permissions.has('users:invite') ?? false;
  const canList = () => session()?.permissions.has('users:list') ?? false;

  return (
    <section>
      <PageHeader
        title="Users"
        actions={
          <Show when={canInvite()}>
            <Button onClick={() => navigate('/admin/users/new')}>Invite user</Button>
          </Show>
        }
      />

      <div class="mx-auto max-w-6xl p-6">
        <Show
          when={canList()}
          fallback={
            <p class="text-sm text-neutral-500">You don't have permission to view users.</p>
          }
        >
          <div class="mb-4 flex gap-3">
            <Input
              placeholder="Search by name or email"
              value={q()}
              onInput={(e) => onSearchInput(e.currentTarget.value)}
              class="w-80"
            />
            <Select
              value={params.status ?? ''}
              onChange={(v) => setParams({ status: v || undefined })}
              options={[
                { value: '', label: 'All statuses' },
                { value: 'pending', label: 'Pending' },
                { value: 'active', label: 'Active' },
                { value: 'disabled', label: 'Disabled' },
              ]}
              class="min-w-36"
            />
          </div>

          <UsersTable filters={filters} />
        </Show>
      </div>
    </section>
  );
}
